import { onAuthStateChanged, signInWithEmailAndPassword, signOut } from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { auth, db } from '../services/firebase';

const AuthContext = createContext();

export const useAuth = () => {
  return useContext(AuthContext);
};

export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const currentUserRef = useRef(currentUser);
  currentUserRef.current = currentUser;
  const [userRole, setUserRole] = useState(null);
  const [userBrand, setUserBrand] = useState(null);
  const [organizationId, setOrganizationId] = useState(null);
  const [mustChangePw, setMustChangePw] = useState(false);
  const [loading, setLoading] = useState(true);

  const fetchUserData = useCallback(async (user) => {
    if (!user) {
      setUserRole(null);
      setUserBrand(null);
      setOrganizationId(null);
      setMustChangePw(false);
      return;
    }

    try {
      const userDoc = await getDoc(doc(db, 'users', user.uid));
      if (userDoc.exists()) {
        const userData = userDoc.data();
        setUserRole(userData.role || null);
        setUserBrand(userData.brand || null);
        setOrganizationId(userData.organizationId);
        setMustChangePw(userData.mustChangePw || false);
      } else {
        setUserRole(null);
        setUserBrand(null);
        setOrganizationId(null);
        setMustChangePw(false);
      }
    } catch (e) {
      console.error('Failed to fetch user profile', e);
      setUserRole(null);
      setUserBrand(null);
      setOrganizationId(null);
      setMustChangePw(false);
    }
  }, []);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      setCurrentUser(user);
      await fetchUserData(user);
      setLoading(false);
    });

    return unsubscribe;
  }, [fetchUserData]);

  const login = useCallback(async (email, password) => {
    const credential = await signInWithEmailAndPassword(auth, email, password);
    // 同じ UID での再ログインでは onAuthStateChanged が通知されないため、User も更新する。
    currentUserRef.current = credential.user;
    setCurrentUser(credential.user);
    return credential;
  }, []);

  const logout = useCallback(() => {
    return signOut(auth);
  }, []);

  // currentUserRef経由でアクセスすることで、refreshUserDataの参照を安定化
  // これによりvalueのuseMemoが不必要に再計算されるのを防ぐ
  const refreshUserData = useCallback(async () => {
    if (currentUserRef.current) {
      await fetchUserData(currentUserRef.current);
    }
  }, [fetchUserData]);

  const value = useMemo(
    () => ({
      currentUser,
      userRole,
      userBrand,
      organizationId,
      mustChangePw,
      login,
      logout,
      refreshUserData,
    }),
    [currentUser, userRole, userBrand, organizationId, mustChangePw, login, logout, refreshUserData]
  );

  return <AuthContext.Provider value={value}>{!loading && children}</AuthContext.Provider>;
};
